// 提单BL 대조 보조 — 托盘号 범위 펼치기 · 品名 줄/ETC 판정 (2026-10-09 YZY). 순수 함수(의존성 없음)
// BL 은 공간이 좁아 托盘号를 「RUBY1009-01 ~ RUBY1009-24」 로 합쳐 적고, 品名은 일부만 적은 뒤 마지막에 ETC 를 붙인다.
const K = s => String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]/g, '')

// 「PFX-NN ~ PFX-MM」·「PFX-NN ~ MM」 범위를 펼친 키 집합. 접두어가 다르거나 역순·500개 초과면 펼치지 않는다(ambiguous 에 사유)
const RANGE = /([A-Z][A-Z0-9]*)-(\d+)\s*(?:~|～|–|—|-|至|TO)\s*(?:([A-Z][A-Z0-9]*)-)?(\d+)/gi
export function expandMarks(marks) {
  const keys = new Set()
  const ambiguous = []
  // 하이픈·물결 변형(‐‑‒–—―−－ ∼〜)을 보통 문자로 맞춘다 — 판독 결과에 따라 섞여 나온다(2026-10-18 吴亚欣 #68)
  const s = String(marks == null ? '' : marks).replace(/[‐-―−－]/g, '-').replace(/[∼〜～]/g, '~')
  let m
  RANGE.lastIndex = 0
  while ((m = RANGE.exec(s))) {
    const [raw, p1, a, p2, b] = m
    if (p2 && K(p2) !== K(p1)) { ambiguous.push(raw + ' (접두어 다름)'); continue }
    const lo = parseInt(a, 10); const hi = parseInt(b, 10)
    if (!(hi >= lo) || hi - lo > 500) { ambiguous.push(raw + ' (범위 모호)'); continue }
    const width = a.length
    for (let n = lo; n <= hi; n++) keys.add(K(p1 + String(n).padStart(width, '0')))
  }
  return { keys, ambiguous }
}

/** 托盘号 대조 — ids(우리 쪽 번호들) 가 BL Marks 에 있나. 원문에 있으면 그대로, 없으면 범위 펼친 것에서 찾는다 */
export function matchPalletMarks(ids, marks) {
  const mKey = K(marks)
  const { keys, ambiguous } = expandMarks(marks)
  let viaRange = false
  const missing = ids.filter(id => {
    if (mKey.includes(K(id))) return false
    if (keys.has(K(id))) { viaRange = true; return false }
    return true
  })
  return { missing, viaRange, ambiguous }
}

/**
 * 散货 唛头 대조 — 박스 번호가 여러 개여도 첫 번호와 끝 번호만 본다(2026-10-19 吴亚欣).
 * BL 은 「Z-woosongFT101616~101622-1」 처럼 끝 번호의 앞부분(접두어)을 생략하기도 해서, 끝 번호는 접두어를 뗀 꼴로도 찾는다.
 * codes = 한 散货 托盘의 箱号 목록(순서대로). 반환 { first, last, missing[] }
 */
export function matchLooseMarks(codes, marks) {
  const list = (codes || []).map(c => String(c == null ? '' : c).trim()).filter(Boolean)
  if (!list.length) return { first: '', last: '', missing: [] }
  const mKey = K(marks)
  const first = list[0]
  const last = list[list.length - 1]
  const tail = (String(last).match(/^\D*(\d.*)$/) || [])[1] || ''
  const missing = []
  if (!mKey.includes(K(first))) missing.push(first)
  if (last !== first && !mKey.includes(K(last)) && !(tail && mKey.includes(K(tail)))) missing.push(last)
  return { first, last, missing }
}

const isHeader = l => /^(SAID\s+TO\s+CONTAIN|DESCRIPTION\s+OF\s+GOODS|SHIPPER'?S?\s+LOAD)[\s:：]*$/i.test(l.trim())
const isEtc = l => /^ETC\.?$/i.test(l.trim())
// 관사(A/AN/THE) 를 뗀 비교키
const dk = s => K(String(s == null ? '' : s).replace(/^\s*(?:an?|the)\s+/i, ''))

/**
 * 品名 대조 — 반환 status: ok | warn | diff, note: 판정 이유(쉬운 말)
 *  ① 인보이스 품명이 BL 글에 다 있다(기존 규칙) → ok
 *  ② BL 줄 전부가 인보이스 품명 집합에 있고, 마지막 줄이 ETC 면(ETC 줄은 무시) → ok
 *  ③ BL 품명 줄(머리말·ETC 제외) 중 하나라도 인보이스 품명과 겹치면 → ok (2026-10-19 吴亚欣 — 提单에는 일부 품명만 적힌다)
 *  ④ 그 밖: 기존대로 일부만 있으면 warn, 하나도 없으면 diff
 */
export function matchDescription(descs, blText) {
  const dKey = K(blText)
  const missing = descs.filter(d => !dKey.includes(K(d)))
  if (!missing.length) return { status: 'ok', note: '' }
  const legacy = missing.length < descs.length ? 'warn' : 'diff'
  // 「SAID TO CONTAIN:」 같은 머리말 줄은 품명이 아니다(2026-10-18 吴亚欣 #68)
  const lines = String(blText == null ? '' : blText).split(/\r?\n/).map(l => l.trim()).filter(l => l && !isHeader(l))
  const keys = descs.map(dk)
  // BL 은 대표 품명만 짧게 적는다(「BRACELET」 ↔ 인보이스 「Gemstone bracelet」) — 한쪽이 다른 쪽에 들어 있으면 같은 품목으로 본다
  const inInvoice = l => { const k = dk(l); return k.length >= 4 && keys.some(d => d === k || d.includes(k) || k.includes(d)) }
  if (lines.length && isEtc(lines[lines.length - 1])) {
    const body = lines.slice(0, -1)
    if (body.length && body.every(inInvoice)) return { status: 'ok', note: 'ETC 줄 허용 — 提单 品名이 모두 箱单发票에 있음' }
  }
  // 提单 品名 줄(ETC 제외) 중 하나라도 箱单发票 품명과 겹치면 일치(2026-10-19 吴亚欣 — 提单에는 일부 品名만 적힌다)
  if (lines.filter(l => !isEtc(l)).some(inInvoice)) return { status: 'ok', note: '提单 品名 중 箱单发票와 겹치는 품명 있음' }
  return { status: legacy, note: '' }
}
